import { useEffect, useRef, useState } from 'react';


async function uploadImage(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const base64Data = reader.result.split(',')[1];
        const response = await fetch(process.env.REACT_APP_API_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            action: 'uploadCmsImage',
            base64Data,
            mimeType: file.type || 'image/png',
            fileName: `${Date.now()}-${file.name}`
          })
        });
        const data = await response.json();
        if (data.error) throw new Error(data.error);
        resolve(data.url);
      } catch (err) {
        reject(err);
      }
    };
    reader.onerror = () => reject(new Error('Failed to read file.'));
    reader.readAsDataURL(file);
  });
}


// STRUCTURED_RICH_TEXT_CARET_FIX_V1

export default function RichTextEditor({
  value,
  onChange,
  onBlur,
  readOnly = false
}) {
  const [html, setHtml] = useState(value || '');
  const [htmlMode, setHtmlMode] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');

  const visualEditorRef = useRef(null);
  const htmlTextareaRef = useRef(null);
  const imageInputRef = useRef(null);

  const savedVisualRangeRef = useRef(null);
  const savedHtmlSelectionRef = useRef({
    start: 0,
    end: 0
  });

  /*
   * The visual editor is contentEditable rather than a normal
   * controlled React input.
   *
   * Reassigning innerHTML while the user is editing destroys the
   * browser's live selection/caret in desktop Chromium. Structured
   * Form autosave makes parent value updates frequent enough that
   * this can otherwise look like the cursor jumping to the start.
   *
   * Keep refs for the editor-owned HTML and for the last value sent
   * to the parent. A parent echo of our own edit is not an external
   * change and must never cause the live DOM to be rewritten.
   */
  const htmlRef =
    useRef(
      value || ''
    );

  const lastEmittedHtmlRef =
    useRef(
      value || ''
    );

  /*
   * Parent echoes must never rewrite whichever editor the user is
   * actively typing in. The local editor remains authoritative
   * until blur.
   */
  useEffect(() => {
    const nextHtml =
      value ||
      '';

    if (
      nextHtml ===
      lastEmittedHtmlRef.current
    ) {
      return;
    }

    const activeElement =
      document.activeElement;

    const activelyEditing =
      activeElement ===
        visualEditorRef.current ||
      activeElement ===
        htmlTextareaRef.current;

    if (
      activelyEditing
    ) {
      return;
    }

    htmlRef.current =
      nextHtml;

    setHtml(
      currentHtml =>
        currentHtml ===
        nextHtml
          ? currentHtml
          : nextHtml
    );

    if (
      !htmlMode &&
      !readOnly &&
      visualEditorRef.current &&
      visualEditorRef
        .current
        .innerHTML !==
        nextHtml
    ) {
      visualEditorRef
        .current
        .innerHTML =
        nextHtml;
    }
  }, [
    value,
    htmlMode,
    readOnly
  ]);


  /*
   * Initial population and transitions back into visual mode.
   * Local keystrokes do not run through this effect.
   */
  useEffect(() => {
    if (
      htmlMode ||
      readOnly ||
      !visualEditorRef.current
    ) {
      return;
    }

    const editor =
      visualEditorRef.current;

    if (
      editor.innerHTML !==
      htmlRef.current
    ) {
      editor.innerHTML =
        htmlRef.current;
    }
  }, [
    htmlMode,
    readOnly
  ]);


  function commitHtml(
    nextHtml
  ) {
    htmlRef.current =
      nextHtml;

    lastEmittedHtmlRef.current =
      nextHtml;

    setHtml(
      nextHtml
    );

    if (onChange) {
      onChange(
        nextHtml
      );
    }
  }


  function handleVisualBlur() {
    saveVisualSelection();

    if (onBlur) {
      onBlur();
    }
  }


  function handleHtmlBlur() {
    saveHtmlSelection();

    if (onBlur) {
      onBlur();
    }
  }


  function syncFromVisualEditor() {
    if (!visualEditorRef.current) return;

    const nextHtml = visualEditorRef.current.innerHTML;
    commitHtml(nextHtml);
  }


  // ----------------------------------------------------------------
  // VISUAL EDITOR SELECTION
  // ----------------------------------------------------------------

  function saveVisualSelection() {
    if (!visualEditorRef.current) return;

    const selection = window.getSelection();

    if (!selection || selection.rangeCount === 0) {
      return;
    }

    const range = selection.getRangeAt(0);

    if (
      visualEditorRef.current.contains(range.commonAncestorContainer)
    ) {
      savedVisualRangeRef.current = range.cloneRange();
    }
  }


  function restoreVisualSelection() {
    if (!visualEditorRef.current) return null;

    const selection = window.getSelection();

    if (!selection) return null;

    let range = savedVisualRangeRef.current;

    if (range) {
      try {
        selection.removeAllRanges();
        selection.addRange(range);
        return range;
      } catch (err) {
        // The stored DOM range may no longer exist after an HTML-mode
        // edit. In that case, fall through and place the cursor at end.
      }
    }

    range = document.createRange();
    range.selectNodeContents(visualEditorRef.current);
    range.collapse(false);

    selection.removeAllRanges();
    selection.addRange(range);

    return range;
  }


  function focusVisualEditor() {
    if (!visualEditorRef.current) return;

    visualEditorRef.current.focus();
    restoreVisualSelection();
  }


  // ----------------------------------------------------------------
  // TOOLBAR FORMATTING
  // ----------------------------------------------------------------

  function runVisualCommand(command) {
    focusVisualEditor();

    // execCommand is used deliberately here as a lightweight browser
    // HTML editing mechanism. There is no separate document schema.
    document.execCommand('styleWithCSS', false, false);
    document.execCommand(command, false, null);

    syncFromVisualEditor();
    saveVisualSelection();
  }


  function wrapHtmlSelection(openTag, closeTag) {
    const textarea = htmlTextareaRef.current;

    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = html.substring(start, end);

    const replacement =
      openTag +
      selectedText +
      closeTag;

    const nextHtml =
      html.substring(0, start) +
      replacement +
      html.substring(end);

    commitHtml(nextHtml);

    requestAnimationFrame(() => {
      textarea.focus();

      if (selectedText) {
        textarea.setSelectionRange(
          start + openTag.length,
          end + openTag.length
        );
      } else {
        textarea.setSelectionRange(
          start + openTag.length,
          start + openTag.length
        );
      }

      saveHtmlSelection();
    });
  }


  function applyHtmlList(type) {
    const textarea = htmlTextareaRef.current;

    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = html.substring(start, end);

    let replacement;

    if (selectedText) {
      const lines = selectedText.split(/\r?\n/);

      const items = lines
        .map((line) => `  <li>${line}</li>`)
        .join('\n');

      replacement =
        `<${type}>\n` +
        `${items}\n` +
        `</${type}>`;
    } else {
      replacement =
        `<${type}>\n` +
        `  <li></li>\n` +
        `</${type}>`;
    }

    const nextHtml =
      html.substring(0, start) +
      replacement +
      html.substring(end);

    commitHtml(nextHtml);

    requestAnimationFrame(() => {
      textarea.focus();

      if (!selectedText) {
        const cursorPosition =
          start +
          `<${type}>\n  <li>`.length;

        textarea.setSelectionRange(
          cursorPosition,
          cursorPosition
        );
      } else {
        textarea.setSelectionRange(
          start,
          start + replacement.length
        );
      }

      saveHtmlSelection();
    });
  }


  function handleBold() {
    if (htmlMode) {
      wrapHtmlSelection('<strong>', '</strong>');
    } else {
      runVisualCommand('bold');
    }
  }


  function handleItalic() {
    if (htmlMode) {
      wrapHtmlSelection('<em>', '</em>');
    } else {
      runVisualCommand('italic');
    }
  }


  function handleStrike() {
    if (htmlMode) {
      wrapHtmlSelection('<s>', '</s>');
    } else {
      runVisualCommand('strikeThrough');
    }
  }

    function handleClearFormatting() {
    if (htmlMode) return;

    focusVisualEditor();

    document.execCommand(
      'removeFormat',
      false,
      null
    );

    // removeFormat does not reliably remove links
    document.execCommand(
      'unlink',
      false,
      null
    );

    syncFromVisualEditor();
    saveVisualSelection();
  }

  function handleBulletList() {
    if (htmlMode) {
      applyHtmlList('ul');
    } else {
      runVisualCommand('insertUnorderedList');
    }
  }


  function handleOrderedList() {
    if (htmlMode) {
      applyHtmlList('ol');
    } else {
      runVisualCommand('insertOrderedList');
    }
  }

  function normalizeLinkUrl(value) {
  const url =
    String(value || '').trim();

  if (!url) {
    return '';
  }

  if (
    /^(https?:\/\/|mailto:|tel:|#|\/)/i.test(
      url
    )
  ) {
    return url;
  }

  return `https://${url}`;
}


function escapeLinkAttribute(value) {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}


function handleLinkButtonMouseDown(e) {
  e.preventDefault();

  if (htmlMode) {
    saveHtmlSelection();
  } else {
    saveVisualSelection();
  }
}


function handleLink() {
  if (htmlMode) {
    const {
      start,
      end
    } =
      savedHtmlSelectionRef.current;

    const safeStart =
      Math.min(
        start,
        html.length
      );

    const safeEnd =
      Math.min(
        end,
        html.length
      );

    if (
      safeStart === safeEnd
    ) {
      window.alert(
        'Select the text you want to turn into a link first.'
      );
      return;
    }

    const rawUrl =
      window.prompt(
        'Enter the link address:',
        'https://'
      );

    if (rawUrl === null) {
      return;
    }

    const url =
      normalizeLinkUrl(
        rawUrl
      );

    if (!url) {
      return;
    }

    const selectedText =
      html.substring(
        safeStart,
        safeEnd
      );

    const openTag =
      `<a href="${escapeLinkAttribute(
        url
      )}">`;

    const closeTag =
      '</a>';

    const replacement =
      openTag +
      selectedText +
      closeTag;

    const nextHtml =
      html.substring(
        0,
        safeStart
      ) +
      replacement +
      html.substring(
        safeEnd
      );

    commitHtml(nextHtml);

    requestAnimationFrame(
      () => {
        const textarea =
          htmlTextareaRef.current;

        if (!textarea) {
          return;
        }

        textarea.focus();

        textarea.setSelectionRange(
          safeStart +
            openTag.length,
          safeStart +
            openTag.length +
            selectedText.length
        );

        saveHtmlSelection();
      }
    );

    return;
  }


  const range =
    restoreVisualSelection();

  if (
    !range ||
    range.collapsed
  ) {
    window.alert(
      'Select the text you want to turn into a link first.'
    );
    return;
  }

  const rawUrl =
    window.prompt(
      'Enter the link address:',
      'https://'
    );

  if (rawUrl === null) {
    return;
  }

  const url =
    normalizeLinkUrl(
      rawUrl
    );

  if (!url) {
    return;
  }

  focusVisualEditor();

  document.execCommand(
    'createLink',
    false,
    url
  );

  syncFromVisualEditor();
  saveVisualSelection();
}

  // ----------------------------------------------------------------
  // HTML MODE
  // ----------------------------------------------------------------

  function saveHtmlSelection() {
    const textarea = htmlTextareaRef.current;

    if (!textarea) return;

    savedHtmlSelectionRef.current = {
      start: textarea.selectionStart,
      end: textarea.selectionEnd
    };
  }


  function handleRawHtmlChange(e) {
    const nextHtml = e.target.value;

    commitHtml(nextHtml);

    savedHtmlSelectionRef.current = {
      start: e.target.selectionStart,
      end: e.target.selectionEnd
    };
  }


  function handleToggleHtmlMode() {
    if (!htmlMode) {
      // Before entering source mode, make sure any visual DOM changes
      // have been copied into the canonical HTML.
      if (visualEditorRef.current) {
        const currentHtml = visualEditorRef.current.innerHTML;

        if (currentHtml !== html) {
          commitHtml(currentHtml);
        }
      }

      setHtmlMode(true);
    } else {
      // No parsing/conversion occurs here.
      // The visual editor simply receives the same HTML string.
      setHtmlMode(false);
    }
  }


  // ----------------------------------------------------------------
  // IMAGE INSERTION
  // ----------------------------------------------------------------

  function handleImageButtonMouseDown(e) {
    // Prevent the toolbar button itself from destroying the current
    // editing selection.
    e.preventDefault();

    if (htmlMode) {
      saveHtmlSelection();
    } else {
      saveVisualSelection();
    }
  }


  function handleImageButtonClick() {
    imageInputRef.current?.click();
  }


  function insertImageIntoHtml(url) {
    const tag =
      `<img src="${url}" alt="" ` +
      `style="max-width:100%; height:auto;" />`;

    const textarea = htmlTextareaRef.current;

    const {
      start,
      end
    } = savedHtmlSelectionRef.current;

    const safeStart = Math.min(start, html.length);
    const safeEnd = Math.min(end, html.length);

    const nextHtml =
      html.substring(0, safeStart) +
      tag +
      html.substring(safeEnd);

    commitHtml(nextHtml);

    requestAnimationFrame(() => {
      if (!textarea) return;

      const cursorPosition = safeStart + tag.length;

      textarea.focus();
      textarea.setSelectionRange(
        cursorPosition,
        cursorPosition
      );

      savedHtmlSelectionRef.current = {
        start: cursorPosition,
        end: cursorPosition
      };
    });
  }


  function insertImageIntoVisualEditor(url) {
    if (!visualEditorRef.current) return;

    visualEditorRef.current.focus();

    const selection = window.getSelection();

    if (!selection) return;

    let range = restoreVisualSelection();

    if (!range) {
      range = document.createRange();
      range.selectNodeContents(visualEditorRef.current);
      range.collapse(false);

      selection.removeAllRanges();
      selection.addRange(range);
    }

    const img = document.createElement('img');

    img.src = url;
    img.alt = '';
    img.style.maxWidth = '100%';
    img.style.height = 'auto';

    range.deleteContents();
    range.insertNode(img);

    // Put the cursor immediately after the new image.
    range.setStartAfter(img);
    range.collapse(true);

    selection.removeAllRanges();
    selection.addRange(range);

    savedVisualRangeRef.current = range.cloneRange();

    syncFromVisualEditor();
  }


  async function handleImageUpload(e) {
    const file = e.target.files?.[0];

    console.log(
      'Image selected:',
      file?.name,
      file?.size
    );

    if (!file) return;

    setUploading(true);
    setUploadError('');

    try {
      console.log('Attempting upload...');

      const url = await uploadImage(file);

      console.log(
        'Upload succeeded, url:',
        url
      );

      if (htmlMode) {
        insertImageIntoHtml(url);
      } else {
        insertImageIntoVisualEditor(url);
      }
    } catch (err) {
      console.error(
        'Image upload failed:',
        err
      );

      setUploadError(
        'Image upload failed. Please try again.'
      );
    } finally {
      setUploading(false);

      if (imageInputRef.current) {
        imageInputRef.current.value = '';
      }
    }
  }


  // ----------------------------------------------------------------
  // READ-ONLY DISPLAY
  // ----------------------------------------------------------------

  if (readOnly) {
    return (
      <div
        style={styles.readOnly}
        dangerouslySetInnerHTML={{
          __html: value || ''
        }}
      />
    );
  }


  // ----------------------------------------------------------------
  // EDITOR
  // ----------------------------------------------------------------

  return (
    <div style={styles.wrapper}>
      <div style={styles.toolbar}>
        <button
          type="button"
          style={styles.toolBtn}
          onMouseDown={(e) => e.preventDefault()}
          onClick={handleBold}
          title="Bold"
        >
          <strong>B</strong>
        </button>

        <button
          type="button"
          style={styles.toolBtn}
          onMouseDown={(e) => e.preventDefault()}
          onClick={handleItalic}
          title="Italic"
        >
          <em>I</em>
        </button>

                <button
          type="button"
          style={styles.toolBtn}
          onMouseDown={(e) => e.preventDefault()}
          onClick={handleStrike}
          title="Strikethrough"
        >
          <s>S</s>
        </button>

        <button
          type="button"
          style={styles.toolBtn}
          onMouseDown={(e) => e.preventDefault()}
          onClick={handleClearFormatting}
          disabled={htmlMode}
          title={
            htmlMode
              ? 'Switch to visual mode to clear formatting'
              : 'Clear formatting'
          }
        >
          Clear Formatting
        </button>

        <div style={styles.divider} />

        <button
          type="button"
          style={styles.toolBtn}
          onMouseDown={(e) => e.preventDefault()}
          onClick={handleBulletList}
          title="Bullet list"
        >
          • List
        </button>

        <button
  type="button"
  style={styles.toolBtn}
  onMouseDown={(e) =>
    e.preventDefault()
  }
  onClick={handleOrderedList}
  title="Numbered list"
>
  1. List
</button>

<div style={styles.divider} />

<button
  type="button"
  style={styles.toolBtn}
  onMouseDown={
    handleLinkButtonMouseDown
  }
  onClick={handleLink}
  title="Turn selected text into a link"
>
  🔗 Link
</button>

<div style={styles.divider} />

        <input
          ref={imageInputRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={handleImageUpload}
          disabled={uploading}
        />

        <button
          type="button"
          style={styles.toolBtn}
          onMouseDown={handleImageButtonMouseDown}
          onClick={handleImageButtonClick}
          disabled={uploading}
          title="Insert image"
        >
          {uploading ? '⏳' : '🖼'}
        </button>

        <div style={styles.divider} />

        <button
          type="button"
          style={{
            ...styles.toolBtn,
            ...(htmlMode
              ? styles.toolBtnActive
              : {})
          }}
          onMouseDown={(e) => e.preventDefault()}
          onClick={handleToggleHtmlMode}
          title={
            htmlMode
              ? 'Switch to visual editor'
              : 'Edit HTML source'
          }
        >
          {'</>'}
        </button>
      </div>

      {uploadError && (
        <div style={styles.uploadError}>
          {uploadError}
        </div>
      )}

      {htmlMode ? (
        <textarea
          ref={htmlTextareaRef}
          style={styles.htmlTextarea}
          value={html}
          onChange={handleRawHtmlChange}
          onSelect={saveHtmlSelection}
          onClick={saveHtmlSelection}
          onKeyUp={saveHtmlSelection}
          onBlur={handleHtmlBlur}
          spellCheck={false}
          placeholder={`Enter HTML here.

Accordion example:
<details>
  <summary>Click to expand</summary>
  <p>Content here.</p>
</details>`}
        />
      ) : (
        <div
          ref={visualEditorRef}
          style={styles.editor}
          contentEditable
          suppressContentEditableWarning
          onInput={syncFromVisualEditor}
          onMouseUp={saveVisualSelection}
          onKeyUp={saveVisualSelection}
          onFocus={() => {
            document.execCommand(
              'defaultParagraphSeparator',
              false,
              'p'
            );
          }}
          onBlur={
            handleVisualBlur
          }
        />
      )}
    </div>
  );
}


const styles = {
  wrapper: {
    border: '1px solid #d7dee5',
    borderRadius: '8px',
    overflow: 'hidden',
    background: 'white'
  },

  readOnly: {
    lineHeight: '1.6',
    color: '#1f2933'
  },

  toolbar: {
    display: 'flex',
    gap: '4px',
    padding: '8px 10px',
    borderBottom: '1px solid #d7dee5',
    background: '#f7f9fa',
    flexWrap: 'wrap',
    alignItems: 'center'
  },

  toolBtn: {
    border: '1px solid transparent',
    background: 'transparent',
    borderRadius: '5px',
    padding: '4px 8px',
    fontSize: '13px',
    cursor: 'pointer',
    color: '#1f2933'
  },

  toolBtnActive: {
    background: '#e7f1f7',
    border: '1px solid #b0cfe0',
    color: '#215b7d'
  },

  divider: {
    width: '1px',
    height: '20px',
    background: '#d7dee5',
    margin: '0 4px'
  },

  editor: {
    padding: '12px',
    minHeight: '120px',
    fontSize: '14px',
    lineHeight: '1.6',
    color: '#1f2933',
    outline: 'none',
    overflowWrap: 'break-word'
  },

  htmlTextarea: {
    width: '100%',
    minHeight: '180px',
    padding: '12px',
    fontSize: '13px',
    fontFamily: 'monospace',
    lineHeight: '1.6',
    color: '#1f2933',
    background: '#fafbfc',
    border: 'none',
    outline: 'none',
    resize: 'vertical',
    boxSizing: 'border-box'
  },

  uploadError: {
    padding: '6px 12px',
    background: '#faeaea',
    color: '#a12a2a',
    fontSize: '13px',
    borderBottom: '1px solid #e8bcbc'
  }
};